import React, { useMemo, useState } from "react";
import { Eye, EyeSlash, Stack, ArrowsClockwise } from "@phosphor-icons/react";

import { useI18n } from "@/lib/i18n";
const COLOR_MAP = {
  "24": "#38BDF8", "27": "#0EA5E9", "32": "#0284C7",
  "40": "#22C55E", "42": "#16A34A", "43": "#15803D",
  "48": "#F59E0B", "49": "#D97706", "50": "#B45309",
  "55": "#EF4444", "58": "#DC2626", "65": "#B91C1C",
  "70": "#8B5CF6", "75": "#7C3AED",
};

/**
 * LayerPanel — Manuel Palet Düzenleme yardımcısı.
 *
 * Kutuları Z tabanına göre KATMANLARA gruplar, her katman için:
 *  - Görünürlük toggle (3D'de gizle/göster)
 *  - Seçim (o katmanın 2D top-down grid'ini gösterir)
 *  - Katmandaki kutu sayısı, toplam yükseklik, toplam ağırlık
 */
export default function LayerPanel({
  pallet, L, W, hiddenLayerKeys, setHiddenLayerKeys,
  selectedIdx, onSelectBox,
}) {
  const { t } = useI18n();
  const boxes = pallet?.boxes || [];

  // Katmanları hesapla: z değerine göre grupla (1cm tolerans)
  const layers = useMemo(() => {
    const map = new Map();
    boxes.forEach((b, i) => {
      const key = Math.round(b.z);
      if (!map.has(key)) map.set(key, { z: key, boxes: [], indices: [] });
      map.get(key).boxes.push(b);
      map.get(key).indices.push(i);
    });
    return Array.from(map.values()).sort((a, b) => a.z - b.z);
  }, [boxes]);

  const [selectedLayerZ, setSelectedLayerZ] = useState(null);
  const activeLayer = useMemo(() => {
    if (selectedLayerZ != null) return layers.find((l) => l.z === selectedLayerZ) || null;
    return layers[0] || null;
  }, [layers, selectedLayerZ]);

  const totalKg = boxes.reduce((s, b) => s + (b.net_kg || 0), 0);
  const topH = boxes.length ? Math.max(...boxes.map((b) => b.z + b.dz)) : 0;

  const toggleLayer = (z) => {
    const next = new Set(hiddenLayerKeys);
    if (next.has(z)) next.delete(z); else next.add(z);
    setHiddenLayerKeys(Array.from(next));
  };

  const showAll = () => setHiddenLayerKeys([]);

  return (
    <div className="bg-white border border-slate-200" data-testid="layer-panel">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <div className="flex items-center gap-2">
          <Stack size={16} weight="duotone" className="text-[#2563EB]" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">Katmanlar</h3>
        </div>
        <button onClick={showAll} data-testid="layers-show-all"
          className="text-[10px] text-slate-500 hover:text-[#2563EB] inline-flex items-center gap-1">
          <ArrowsClockwise size={11} /> {t("final.layer.show_all")}
        </button>
      </div>

      {/* Layer live summary */}
      <div className="grid grid-cols-3 border-b border-slate-100 text-center">
        <div className="p-2">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Katman</div>
          <div className="font-display font-bold text-lg" data-testid="layer-count">{layers.length}</div>
        </div>
        <div className="p-2 border-l border-slate-100">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">{t("layer.total_h")}</div>
          <div className="font-display font-bold text-lg" data-testid="layer-total-height">{topH.toFixed(1)} <span className="text-xs text-slate-500">cm</span></div>
        </div>
        <div className="p-2 border-l border-slate-100">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">{t("layer.total_kg")}</div>
          <div className="font-display font-bold text-lg" data-testid="layer-total-weight">{totalKg.toFixed(1)}</div>
        </div>
      </div>

      {/* Layer list */}
      <div className="max-h-56 overflow-y-auto">
        {layers.map((l, li) => {
          const hidden = hiddenLayerKeys.includes(l.z);
          const active = activeLayer && activeLayer.z === l.z;
          const layerKg = l.boxes.reduce((s, b) => s + (b.net_kg || 0), 0);
          const layerH = Math.max(...l.boxes.map((b) => b.dz));
          return (
            <div key={l.z}
              data-testid={`layer-row-${li}`}
              className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer ${active ? "bg-blue-50" : "hover:bg-slate-50"}`}
              onClick={() => setSelectedLayerZ(l.z)}>
              <button onClick={(e) => { e.stopPropagation(); toggleLayer(l.z); }}
                data-testid={`layer-toggle-${li}`}
                className={`w-6 h-6 flex items-center justify-center border ${hidden ? "border-slate-300 text-slate-400" : "border-[#2563EB] text-[#2563EB] bg-blue-50"}`}
                title={hidden ? t("layer.show_3d") : t("layer.hide_3d")}>
                {hidden ? <EyeSlash size={12} /> : <Eye size={12} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Katman {li + 1}</div>
                <div className="text-[10px] font-mono text-slate-500">Z={l.z}cm · {l.boxes.length} kutu · h≈{layerH}cm · {layerKg.toFixed(1)}kg</div>
              </div>
            </div>
          );
        })}
        {layers.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-6">{t("layer.no_boxes")}</div>
        )}
      </div>

      {/* 2D Top-down view of active layer */}
      {activeLayer && (
        <div className="border-t border-slate-200 p-3" data-testid="layer-topdown">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            {t("layer.top_view")} (Z={activeLayer.z}cm)
          </div>
          <TopDown2D
            L={L} W={W}
            boxes={activeLayer.boxes}
            indices={activeLayer.indices}
            selectedIdx={selectedIdx}
            onSelectBox={onSelectBox}
          />
          <div className="mt-1 text-[10px] text-slate-500 text-center font-mono">
            Palet: {L}×{W} cm — {t("layer.click_box")}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 2D top-down SVG view of a single layer.
 * Renders palet outline + rectangles for each box, colored by inch.
 */
function TopDown2D({ L, W, boxes, indices, selectedIdx, onSelectBox }) {
  // SVG viewbox = pallet dimensions (cm). We flip Y so 0 is bottom.
  const scale = 1;
  const pad = 4;
  const vbW = L + pad * 2;
  const vbH = W + pad * 2;
  return (
    <svg viewBox={`0 0 ${vbW} ${vbH}`} className="w-full h-auto bg-slate-50 border border-slate-200" data-testid="topdown-svg">
      {/* Palet outline */}
      <rect x={pad} y={pad} width={L} height={W} fill="#F1F5F9" stroke="#0F172A" strokeWidth={1} />
      {/* Grid */}
      {Array.from({ length: Math.floor(L / 10) + 1 }).map((_, i) => (
        <line key={`vx${i}`} x1={pad + i * 10} y1={pad} x2={pad + i * 10} y2={pad + W} stroke="#CBD5E1" strokeWidth={0.15} />
      ))}
      {Array.from({ length: Math.floor(W / 10) + 1 }).map((_, i) => (
        <line key={`hy${i}`} x1={pad} y1={pad + i * 10} x2={pad + L} y2={pad + i * 10} stroke="#CBD5E1" strokeWidth={0.15} />
      ))}
      {/* Boxes */}
      {boxes.map((b, idx) => {
        const color = COLOR_MAP[String(b.inch)] || "#64748B";
        const globalIdx = indices[idx];
        const isSelected = selectedIdx === globalIdx;
        return (
          <g key={idx} onClick={() => onSelectBox && onSelectBox(b, globalIdx)} style={{ cursor: "pointer" }}
             data-testid={`td-box-${globalIdx}`}>
            <rect
              x={pad + b.x * scale}
              y={pad + (W - b.y - b.dy) * scale}
              width={b.dx * scale}
              height={b.dy * scale}
              fill={color}
              stroke={isSelected ? "#FACC15" : "#0F172A"}
              strokeWidth={isSelected ? 1.4 : 0.4}
              opacity={0.85}
            />
            <text
              x={pad + (b.x + b.dx / 2) * scale}
              y={pad + (W - b.y - b.dy / 2) * scale}
              fontSize={Math.max(4, Math.min(b.dx, b.dy) / 4.5)}
              fill="#0F172A"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ pointerEvents: "none", fontFamily: "monospace" }}
            >{b.inch}"</text>
          </g>
        );
      })}
    </svg>
  );
}
